// tabs/ability-section.ts — the Abilities tab's ability-type sections (full/ability/quick/free/
// reaction/curse/sanguine/ability_choice/charged), rendered client-side from the FULL-nested
// ability entries in the caster blob (computed.creature.abilities.<type>, via ftLayerEntries).
//
// The per-row markup now lives in the shared ability-row.ts (buildSection/buildRow), reused by
// the weapons attack tables (weapon-abilities.ts), the passive tree (passive-abilities.ts), and
// the single-section creature mount (renderAbilitySection). This file just owns the section LIST
// + colors for the Abilities tab. Mounted as a hybrid sub-mount inside the PHP Abilities tab
// (_abilities_inc.php → ftMountPcSheet); rows keep .fate-tide-sheet-ability-row +
// data-ft-ability-name so the PHP search filters them.

import type { CachedCaster } from '../types';
import { buildSection, buildIconSection } from './ability-row';
import { casterItemType } from '../read-helpers';
import { renderPilotedAbilityRefusal } from '../piloted-ability-refusal';

// Section list + header colors — verbatim from _abilities_inc.php / LIST_DISPLAY_ABILITY_CONFIG.
const SECTIONS: ReadonlyArray<{ type: string; color: string }> = [
    // Lair powers first (only lairs carry the lair_ability layer; buildSection returns '' for
    // everyone else, so this section is inert on PCs/creatures). See lairPopulateAbilitiesFromCatalog.
    { type: 'lair_ability', color: 'success' },
    { type: 'full_ability', color: 'info' },
    { type: 'ability', color: 'primary' },
    { type: 'quick_ability', color: 'secondary' },
    { type: 'free_ability', color: 'dark' },
    { type: 'reaction_ability', color: 'warning' },
    { type: 'curse', color: 'dark' },
    { type: 'sanguine', color: 'danger' },
    { type: 'ability_choice', color: 'primary' },
    { type: 'charged_ability', color: 'info' },
];
// Header colors per type — includes attack/opportunity (NOT in the Abilities-tab SECTIONS, but
// used by the single-section creature mount). Mirrors LIST_DISPLAY_ABILITY_CONFIG colors.
const TYPE_COLOR: Record<string, string> = {
    ...SECTIONS.reduce((m, s) => { m[s.type] = s.color; return m; }, {} as Record<string, string>),
    attack_ability: 'danger',
    opportunity_attack_ability: 'dark',
};

/** Render all ability-type sections for the Abilities tab from the caster blob. The list↔icon toggle
 *  lives in the PHP Core Abilities banner (a .ft-ability-view-toolbar-host, painted by
 *  ftMountAbilityViewToolbars), NOT here — so this pane just honours the cookied mode: icon mode
 *  collapses each section to a medallion strip (buildIconSection); list mode renders the full tables
 *  (buildSection). A toggle re-renders this pane in place via ftAbilityViewRerender (core-helpers.js). */
export function renderAbilitySections(caster: CachedCaster, _casterId: string, isStatic = false): string {
    // isStatic (item-creature statblock): force read-only — abilities are reference-only, never fire.
    const canEdit = !isStatic && typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    // Static item-creature statblock (item.php?id=<creature>) renders in a HALF-WIDTH col-lg-6 card, so
    // the list tables' fixed 4-column grid (70px btn + clamp source + 175px charges) starves the auto
    // name column → names clip / break mid-word ("Should-er Throw") with the empty source/charges columns
    // sitting blank to the right. ft-pc-abilities--compact reflows those tables to content-sized side
    // columns + a name column that takes the slack (CSS in 95-pc-sheet.css) — the same reflow the weapons
    // tab and side-info host already apply. The full-width live PC sheet (isStatic=false) keeps its grid.
    // Lairs render in the NARROW GM-viewer / side panel, so they hit the SAME name-starve — apply the
    // compact reflow WITHOUT forcing read-only (a lair's GM must still fire its actions, canEdit stays live).
    // The LIVE encounter-creature console (render-creature-card.ts / table_creature_inc.php in the
    // my_campaign GM viewer) is the SAME half-width (~517px) card as the static statblock but arrives with
    // isStatic=false — so it fell through to the fixed grid and starved the name column the moment the 190px
    // damage column appeared (any dice-bearing ability/quick/attack; dice-less free/reaction have no damage
    // column so they fit — that's the "some ability types look borked, others fine" tell).
    //
    // Discriminate via casterItemType() (read-helpers), NOT a direct caster.items.item_type read: a caster
    // blob commonly carries NO top-level `items` bucket (post-reshape item_type rides in
    // encounter_creatures), so the direct read returns '' on exactly the encounter-creature shape and the
    // reflow would silently NOT fire. Only the full-width live PC SHEET (a definitively player_character
    // caster) keeps the fixed grid; EVERY other caster — the narrow creature/lair console (live or static
    // statblock) AND any unknown/malformed shape — gets compact, because a starved fixed grid in a narrow
    // host is DESTRUCTIVE while compact in a wide host is merely cosmetic (the weapons tab already reflows
    // unconditionally on the full PC sheet, proving compact-wide is safe).
    const itemType = casterItemType(caster).trim().toLowerCase();
    const wrapCls = (isStatic || itemType !== 'player_character') ? 'ft-pc-abilities ft-pc-abilities--compact' : 'ft-pc-abilities';
    const mode = typeof ftGetAbilityViewMode === 'function' ? ftGetAbilityViewMode() : 'list';
    if (mode === 'icon') {
        const size = typeof ftGetAbilityIconSize === 'function' ? ftGetAbilityIconSize() : 'm';
        return `<div class="${wrapCls}">${renderPilotedAbilityRefusal(caster)}${SECTIONS.map((s) => buildIconSection(caster, s.type, canEdit, size)).join('')}</div>`;
    }
    return `<div class="${wrapCls}">${renderPilotedAbilityRefusal(caster)}${SECTIONS.map((s) => buildSection(caster, s.type, s.color, canEdit)).join('')}</div>`;
}

/** Render a SINGLE ability-type section (used by the creature list-display mount, Phase D).
 *  Header color is derived from the type (LIST_DISPLAY_ABILITY_CONFIG parity). */
export function renderAbilitySection(caster: CachedCaster, type: string, isStatic = false): string {
    const canEdit = !isStatic && typeof ftHasPlayerPermission === 'function' && ftHasPlayerPermission(caster);
    const section = buildSection(caster, type, TYPE_COLOR[type] || 'primary', canEdit);
    // renderAbilitySection is EXCLUSIVELY the single-section creature/encounter-creature mount
    // (ability_section pane — list_display_helpers.php); the full-width live PC sheet uses
    // renderAbilitySections. Those creature cards / GM-viewer panels are always narrow, so the
    // fixed 4-column table (70px btn + 190px damage + clamp source + 175px charges) starves the
    // auto NAME column the moment the damage/effect column appears — which happens ONLY on
    // dice-bearing types (Blood Drain 5d8, a 3d6 quick ability): the name clips to ~0 and the
    // medallion ↔ damage chip get flung apart, while dice-less types (free/reaction) still fit.
    // Apply the content-sizing reflow UNCONDITIONALLY (same rationale as the weapon-abilities
    // tables) so every ability type lays out consistently regardless of the effect/charges columns.
    // isStatic still drives canEdit (read-only statblock); only the layout is now always compact.
    const wrapCls = 'ft-pc-abilities ft-pc-abilities--compact';
    const refusal = type === 'full_ability' ? renderPilotedAbilityRefusal(caster) : '';
    return section || refusal ? `<div class="${wrapCls}">${refusal}${section}</div>` : '';
}
